import { getLogger } from '@blueberry/observability/logging.js'
import { getOpenReplayTracker } from '@blueberry/observability/open_replay.js'
import { TrackDxEvent } from '../../dx/lib/analytics/dx.js'

const MAX_RETRY_DELAY_MILLISECONDS = 30_000

// If the tab was hidden at least this long (laptop closed, phone locked), the
// connection may have quietly died without the browser noticing — the socket
// still says "open" but nothing will ever arrive on it. We can't tell a dead
// socket from a healthy one, so when the user comes back we just replace it.
// Worst case we replaced a healthy one, which costs almost nothing.
const ZOMBIE_HIDDEN_THRESHOLD_MILLISECONDS = 60_000

export type UseWebSocketOpts<T> = {
  url: string

  /**
   * How long to wait before retrying to connect.
   * Doubles after each failure, up to a 30 second cap. Retries forever.
   * @default 500
   */
  retryTimeMilliseconds?: number

  idleDisconnectTimeoutMilliseconds?: number

  onMessage: (message: T) => void

  onReadyStateChange?: (state: WebSocketReadyState) => void

  onError?: (event: Event) => void

  onParseFailure?: (message: string, error: unknown) => void
}

export type WebSocketReadyState = 'connecting' | 'open' | 'closing' | 'closed'

export type WebSocketState<T> = {
  send: (data: T) => boolean
  teardown: () => void
}

export const useWebSocket = <INCOMING, OUTGOING = null>(
  opts: UseWebSocketOpts<INCOMING>,
): WebSocketState<OUTGOING> => {
  const id = getUniqueId()
  const logger = getLogger('web_sockets', { metadata: { id, url: opts.url } })
  const protocol = window.location.protocol === 'http:' ? 'ws' : 'wss'
  const host = `${protocol}://${window.location.host}` // must be same as origin for cookies

  const openReplayWsHook = getOpenReplayTracker()?.trackWs(opts.url)

  const initialRetryTimeMilliseconds = opts.retryTimeMilliseconds ?? 500
  let retryTimeMilliseconds = initialRetryTimeMilliseconds
  let retryTimer: number | null = null
  let ws: WebSocket | null = null
  // ws consumer manually closed the connection, so we should not retry
  let manuallyClosed = false
  // Asleep because the user stopped interacting. Unlike manuallyClosed this
  // wakes back up: the next interaction reconnects.
  let idleSuspended = false
  let hiddenAtMilliseconds: number | null = null

  const clearRetryTimer = () => {
    if (retryTimer !== null) {
      clearTimeout(retryTimer)
      retryTimer = null
    }
  }

  // For when the connection dropped and we don't know why: wait a bit, try
  // again, and wait longer after each failure so a struggling server isn't
  // hammered by every open tab. reconnectNow is the impatient sibling.
  const scheduleReconnect = () => {
    if (manuallyClosed || idleSuspended || retryTimer !== null) {
      return
    }

    // Jitter so a fleet-wide disconnect (deploy, redis blip) doesn't turn into
    // a synchronized reconnect stampede.
    const delay = retryTimeMilliseconds * (0.75 + Math.random() * 0.5)

    retryTimer = window.setTimeout(() => {
      retryTimer = null

      if (manuallyClosed || idleSuspended) {
        return
      }

      if (ws && ws.readyState == ws.OPEN) {
        logger.info('Skipping retry. Socket is already open')
        return
      }

      retryTimeMilliseconds = Math.min(
        retryTimeMilliseconds * 2,
        MAX_RETRY_DELAY_MILLISECONDS,
      )
      setWebsocket('Attempting to reconnect')
    }, delay)
  }

  // For when the browser just told us something changed (internet came back,
  // user returned to the tab): any retry delay we scheduled while things were
  // broken is now pointless, so skip it and connect right away.
  const reconnectNow = (reason: string) => {
    if (manuallyClosed) {
      return
    }
    clearRetryTimer()
    resetBackoff()
    setWebsocket(reason)
  }

  const getState = (): WebSocketReadyState => {
    if (!ws) return 'closed'
    if (ws.readyState == ws.OPEN) return 'open'
    if (ws.readyState == ws.CLOSING) return 'closing'
    if (ws.readyState == ws.CLOSED) return 'closed'
    if (ws.readyState == ws.CONNECTING) return 'connecting'
    logger.error('Unknown ready state', ws.readyState)
    return 'connecting'
  }

  const resetBackoff = () => {
    retryTimeMilliseconds = initialRetryTimeMilliseconds
  }

  const updateReadyState = () => {
    if (manuallyClosed) {
      logger.info(
        'Skipping ready state notification. Socket was manually closed',
        getState(),
      )
      return
    }

    logger.info('Ready state changed', getState())
    opts.onReadyStateChange?.(getState())
  }

  const onError = (event: Event) => {
    logger.error('received an error', event)
    if (opts.onError) {
      opts.onError(event)
    }
  }

  const onParseFailure =
    opts.onParseFailure ??
    ((event: MessageEvent, e) => {
      logger.error('Failed to parse message', ws?.url, event.data, e)
    })

  const onVisibilityChange = () => {
    if (document.visibilityState === 'hidden') {
      hiddenAtMilliseconds = Date.now()
      return
    }

    const hiddenForMilliseconds =
      hiddenAtMilliseconds === null ? 0 : Date.now() - hiddenAtMilliseconds
    hiddenAtMilliseconds = null

    if (manuallyClosed || idleSuspended) {
      return
    }

    if (getState() !== 'open') {
      reconnectNow('Tab became visible while disconnected')
      return
    }

    // The socket says it's open, but after this long in the background it
    // might be lying — see ZOMBIE_HIDDEN_THRESHOLD_MILLISECONDS.
    if (hiddenForMilliseconds > ZOMBIE_HIDDEN_THRESHOLD_MILLISECONDS) {
      reconnectNow('Reopening possibly-stale socket after returning to tab')
    }
  }

  const onOnline = () => {
    if (manuallyClosed || idleSuspended) {
      return
    }
    if (getState() !== 'open') {
      reconnectNow('Network back online')
    }
  }

  document.addEventListener('visibilitychange', onVisibilityChange)
  window.addEventListener('online', onOnline)

  const cleanups: Array<() => void> = [
    () => document.removeEventListener('visibilitychange', onVisibilityChange),
    () => window.removeEventListener('online', onOnline),
  ]

  const ws_state: WebSocketState<OUTGOING> = {
    teardown: () => {
      manuallyClosed = true
      clearRetryTimer()
      cleanups.forEach((cleanup) => cleanup())
      if (!ws) {
        logger.error('Cannot close websocket, ws is not initialized')
        return
      }
      ws.close()
    },
    send: (data: OUTGOING): boolean => {
      if (!ws) {
        logger.error('Cannot send message, ws is not initialized', data)
        return false
      }
      if (getState() !== 'open') {
        logger.error('Cannot send message, socket not open', data)
        return false
      }
      openReplayWsHook?.('message', JSON.stringify(data), 'up')
      ws.send(JSON.stringify(data))
      return true
    },
  }

  const setWebsocket = (reason: string): WebSocket => {
    logger.info('Setting websocket', {
      currentState: ws ? getState() : 'initializing',
      reason,
    })

    if (ws) {
      ws.onopen = null
      ws.onclose = null
      ws.onerror = null
      ws.onmessage = null
      ws.close()
    }

    updateReadyState()
    ws = new WebSocket(`${host}${opts.url}`)
    updateReadyState()

    ws.onerror = (ev) => {
      updateReadyState()
      openReplayWsHook?.('error', `${ev}`, 'down')
      onError(ev)
    }

    ws.onclose = (event) => {
      if (!manuallyClosed && !idleSuspended) {
        // Report error to OpenReplay & call our track event endpoint to aid in debugging websocket issues for this ticket: https://github.com/sparkmd/blueberry-monorepo/issues/3927
        const metadata = {
          ws_url: ws?.url ?? '',
          code: event.code, // https://developer.mozilla.org/en-US/docs/Web/API/CloseEvent/code
          reason: event.reason,
          wasClean: event.wasClean,
        }
        void TrackDxEvent.webSocketConnectionClosed(metadata)
        const tracker = getOpenReplayTracker()
        if (tracker) {
          tracker.handleError(Error('Websocket connection closed'), metadata)
        }
        scheduleReconnect()
      }

      updateReadyState()
    }

    ws.onopen = () => {
      resetBackoff()
      updateReadyState()
    }

    ws.onmessage = (event) => {
      openReplayWsHook?.('message', event.data, 'down')

      resetBackoff()

      let parseResult: INCOMING
      try {
        parseResult = JSON.parse(event.data)
      } catch (e) {
        // We don't forward messages that cannot be parsed.
        onParseFailure(event.data, e)
        return
      }

      try {
        opts.onMessage(parseResult)
      } catch (e) {
        logger.error('Failed to process message', parseResult, e)
      }
    }

    return ws
  }

  // The sleep/wake pair: after enough time with no user input, close the
  // socket so abandoned tabs stop talking to the server. The next mouse move,
  // key press, or return to the tab wakes it back up — and reconnecting also
  // makes the consumer fetch anything it missed while asleep.
  if (opts.idleDisconnectTimeoutMilliseconds) {
    const maxIdleTimeMilliseconds = opts.idleDisconnectTimeoutMilliseconds
    let idleTimeMilliseconds = 0

    const onActivity = () => {
      idleTimeMilliseconds = 0
      if (idleSuspended && !manuallyClosed) {
        idleSuspended = false
        reconnectNow('Activity after idle disconnect')
      }
    }

    const onVisibleActivity = () => {
      if (document.visibilityState === 'visible') {
        onActivity()
      }
    }

    const activityEvents = ['mousemove', 'keydown', 'pointerdown'] as const
    activityEvents.forEach((eventName) =>
      window.addEventListener(eventName, onActivity, { passive: true }),
    )
    document.addEventListener('visibilitychange', onVisibleActivity)

    const interval = window.setInterval(() => {
      if (idleSuspended) {
        return
      }

      idleTimeMilliseconds += 1000

      if (idleTimeMilliseconds < maxIdleTimeMilliseconds) {
        return
      }

      logger.info('Disconnecting websocket due to inactivity')

      idleSuspended = true
      clearRetryTimer()
      ws?.close()
    }, 1000)

    cleanups.push(
      () => clearInterval(interval),
      () => document.removeEventListener('visibilitychange', onVisibleActivity),
      ...activityEvents.map(
        (eventName) => () => window.removeEventListener(eventName, onActivity),
      ),
    )
  }

  setWebsocket('initialize')

  return ws_state
}

const getUniqueId = () => {
  return Math.random().toString(36).substring(2)
}
